/**
 * Denial Defender — Universal Orthopedic 4-Gate Rules Engine
 *
 * Every resolver follows the strict gate sequence:
 *   Gate 1: Exemption Overrides (bypass)
 *   Gate 2: Coding & Compliance Validator (ICD-10, Biomarkers)
 *   Gate 3: Dynamic Threshold Evaluation (PT/Therapy)
 *   Gate 4: Contextual & Negation-Aware Matching (Imaging/Injections)
 *
 * Returning null = unresolved → forces manual surgeon verification.
 */
import type {
  ClinicalSeverityData,
  TargetAnatomy,
  GateResult,
} from '@/lib/clinicalSeverityTypes';
import type { VulnerabilityGap } from '@/components/surgeon/v2/denial-defender/DenialDefenderOverlay';
import {
  isNegated,
  matchesAnatomy,
  hasAnatomicalContext,
  getRequiredPTWeeks,
  parseDurationWeeks,
  buildCorpus,
  derivePatientDemographics,
  validateICD10,
  inferAnatomyFromCPT,
} from './utils';

// ═══════════════════════════════════════════════════════════════
//  RULE DEFINITION
// ═══════════════════════════════════════════════════════════════

interface RuleDefinition {
  id: string;
  /** CPT codes this rule applies to. Empty = universal. */
  cptCodes: string[];
  /** Anatomies this rule applies to. Empty = all. */
  anatomies: TargetAnatomy[];
  question: (payerName: string) => string;
  fieldType: 'select' | 'toggle';
  options?: string[];
  payerRule: string;
  resolve: (data: ClinicalSeverityData, anatomy: TargetAnatomy) => string | null;
}

// ═══════════════════════════════════════════════════════════════
//  GATE 1: EXEMPTION OVERRIDE HELPERS
// ═══════════════════════════════════════════════════════════════

function hasAcuteExemption(data: ClinicalSeverityData): boolean {
  const f = data.payer_risk_flags;
  if (!f) return false;
  return f.bone_on_bone_exemption || f.acute_fracture_avulsion || f.neurologic_deficit;
}

// ═══════════════════════════════════════════════════════════════
//  GATE 2: CODING & BIOMARKER VALIDATOR
// ═══════════════════════════════════════════════════════════════

function runCodingGate(data: ClinicalSeverityData, anatomy: TargetAnatomy): GateResult[] {
  const results: GateResult[] = [];
  const ctx = data.procedure_context;

  // ICD-10 validation
  if (ctx?.icd_10_codes?.length) {
    const icdResult = validateICD10(ctx.icd_10_codes, ctx.laterality || 'n/a');
    if (!icdResult.valid) {
      results.push({
        gate: 'coding',
        passed: false,
        reason: icdResult.errors.join('; '),
      });
    }
  }

  // Biomarker: HbA1c for total joint replacements
  const totalJointCpts = new Set(['27447', '27446', '27130', '27132', '23472', '23470']);
  const primaryCpt = ctx?.primary_cpt || '';
  if (totalJointCpts.has(primaryCpt) && data.diabetic) {
    if (data.hba1c === null || data.hba1c === undefined) {
      results.push({
        gate: 'coding',
        passed: false,
        reason: 'HbA1c value required for diabetic patients undergoing total joint replacement.',
      });
    } else if (data.hba1c >= 8.0) {
      results.push({
        gate: 'coding',
        passed: false,
        reason: `HbA1c ${data.hba1c} ≥ 8.0. Payers may deny. Surgical clearance required.`,
      });
    }
  }

  return results;
}

// ═══════════════════════════════════════════════════════════════
//  UNIVERSAL PT / CONSERVATIVE THERAPY RULE
// ═══════════════════════════════════════════════════════════════

const UNIVERSAL_PT_DURATION: RuleDefinition = {
  id: 'gap-pt-duration',
  cptCodes: [], // Universal — applies to all surgical CPTs
  anatomies: [],
  question: (payer) =>
    `${payer} requires documentation of conservative therapy duration. How many weeks of PT/NSAIDs did the patient complete?`,
  fieldType: 'select',
  options: ['4 weeks', '6 weeks', '8 weeks', '12+ weeks'],
  payerRule: 'Conservative Therapy Duration Threshold',
  resolve: (data, anatomy) => {
    // ── Gate 1: Exemption Override ──
    if (hasAcuteExemption(data)) return '12+ weeks';
    if (data.payer_risk_flags?.extended_therapy_met) return '12+ weeks';

    // ── Gate 3: Dynamic Threshold ──
    const patient = derivePatientDemographics(data);
    const requiredWeeks = getRequiredPTWeeks(patient, anatomy);

    const ptEntries = data.conservative_therapy?.filter(t => {
      const m = t.modality.toLowerCase();
      return m.includes('physical therapy') || m.includes('pt') ||
        m.includes('rehab') || m.includes('nsaid') || m.includes('anti-inflammatory');
    });

    if (!ptEntries || ptEntries.length === 0) return null;

    // Find longest documented duration
    const longestDuration = ptEntries.reduce<number | null>((max, entry) => {
      const weeks = entry.duration_weeks ?? parseDurationWeeks(entry.duration);
      if (weeks === null) return max;
      return max === null ? weeks : Math.max(max, weeks);
    }, null);

    if (longestDuration === null) return null;
    if (longestDuration < requiredWeeks) return null; // Threshold not met → manual

    // ── Gate 4: Negation Check ──
    const corpus = buildCorpus(data);
    if (isNegated(corpus, 'physical therapy') || isNegated(corpus, 'pt')) return null;

    if (longestDuration <= 4) return '4 weeks';
    if (longestDuration <= 6) return '6 weeks';
    if (longestDuration <= 8) return '8 weeks';
    return '12+ weeks';
  },
};

// ═══════════════════════════════════════════════════════════════
//  UNIVERSAL IMAGING RULE
// ═══════════════════════════════════════════════════════════════

const UNIVERSAL_IMAGING: RuleDefinition = {
  id: 'gap-imaging',
  cptCodes: [],
  anatomies: [],
  question: () => 'Does advanced imaging (MRI/CT/X-ray) confirm pathology correlating with symptoms?',
  fieldType: 'toggle',
  payerRule: 'Radiographic Evidence Requirement',
  resolve: (data, anatomy) => {
    // ── Gate 1: Exemption Override ──
    if (data.payer_risk_flags?.bone_on_bone_exemption) return 'yes';
    if (data.payer_risk_flags?.neurologic_deficit) return 'yes';

    const findings = data.radiographic_findings;
    if (!findings) return null;

    const specifics = (findings.specific_findings || []).join(' ').toLowerCase();
    const grade = (findings.severity_grade || '').toLowerCase();
    const allText = `${specifics} ${grade}`;

    // ── Gate 4: Anatomy Lock ──
    if (anatomy !== 'unspecified' && !matchesAnatomy(allText, anatomy)) {
      // Check corpus too — findings may reference anatomy elsewhere
      const corpus = buildCorpus(data);
      if (!matchesAnatomy(corpus, anatomy)) return null;
    }

    // ── Gate 4: Quantitative triggers ONLY — NO ambiguous terms ──
    // VALID: definitive quantitative findings
    const definitivePatterns = [
      /full[- ]thickness/i,
      /complete\s+(?:tear|rupture)/i,
      /retract(?:ed|ion)/i,
      /avulsion/i,
      /bone[- ]on[- ]bone/i,
      /kl\s*(?:grade\s*)?[34]/i,
      /grade\s*[34]/i,
      /kellgren[- ]?lawrence\s*[34]/i,
      /(?:>|greater than)\s*50\s*%/i,
      /high[- ]grade\s+partial/i,
      /(?:severe|moderate)\s+(?:central|lateral|foraminal)?\s*stenosis/i,
      /nerve\s+root\s+compression/i,
      /cord\s+compression/i,
      /herniat(?:ed|ion)/i,
      /extrusion/i,
      /cauda\s+equina/i,
      /myelopathy/i,
      /foot\s+drop/i,
    ];

    const hasDefinitive = definitivePatterns.some(rx => rx.test(allText));
    if (!hasDefinitive) {
      // Check corpus for definitive triggers
      const corpus = buildCorpus(data);
      const hasCorpusDefinitive = definitivePatterns.some(rx => rx.test(corpus));
      if (!hasCorpusDefinitive) return null; // Only ambiguous findings → manual
    }

    // ── Gate 4: Negation check ──
    const corpus = buildCorpus(data);
    const negationKeywords = ['stenosis', 'compression', 'herniat', 'tear', 'bone.on.bone', 'grade 3', 'grade 4'];
    if (negationKeywords.some(kw => isNegated(corpus, kw))) return null;

    return 'yes';
  },
};

// ═══════════════════════════════════════════════════════════════
//  UNIVERSAL INJECTION RULE
// ═══════════════════════════════════════════════════════════════

const UNIVERSAL_INJECTION: RuleDefinition = {
  id: 'gap-injection',
  cptCodes: [],
  anatomies: ['shoulder', 'knee', 'hip'], // Injections not typical for foot/hand/spine pre-auth
  question: () => 'What type of injection was administered prior to surgical recommendation?',
  fieldType: 'select',
  options: ['Cortisone (Triamcinolone)', 'Hyaluronic Acid (Synvisc)', 'PRP', 'None administered'],
  payerRule: 'Pre-Surgical Injection Protocol',
  resolve: (data, anatomy) => {
    // ── Gate 1: Exemption Override ──
    // Return null so the narrative uses the CONSERVATIVE THERAPY EXEMPTION paragraph
    // instead of fabricating a clinical injection that never occurred.
    if (hasAcuteExemption(data)) return null;

    const injections = data.injection_history;
    if (!injections || injections.length === 0) return null;

    const corpus = buildCorpus(data);

    const steroidKeywords = ['corticosteroid', 'cortisone', 'triamcinolone', 'depo-medrol',
      'kenalog', 'methylprednisolone', 'betamethasone', 'dexamethasone', 'steroid'];
    const haKeywords = ['hyaluronic', 'synvisc', 'viscosupplementation', 'euflexxa', 'gel-one', 'supartz'];
    const prpKeywords = ['prp', 'platelet-rich', 'platelet rich'];

    for (const inj of injections) {
      const type = inj.type.toLowerCase();
      const location = (inj.location || '').toLowerCase();
      const injText = `${type} ${location}`;

      // ── Gate 4: Anatomy Lock ──
      const isCorrectAnatomy = !location ||
        matchesAnatomy(injText, anatomy) ||
        hasAnatomicalContext(corpus, type.split(' ')[0], anatomy);
      if (!isCorrectAnatomy) continue;

      // Check steroid
      if (steroidKeywords.some(kw => injText.includes(kw))) {
        if (!isNegated(corpus, 'cortisone') && !isNegated(corpus, 'steroid') && !isNegated(corpus, 'injection'))
          return 'Cortisone (Triamcinolone)';
      }

      // Check HA
      if (haKeywords.some(kw => injText.includes(kw))) {
        if (!isNegated(corpus, 'hyaluronic') && !isNegated(corpus, 'viscosupplementation'))
          return 'Hyaluronic Acid (Synvisc)';
      }

      // Check PRP
      if (prpKeywords.some(kw => injText.includes(kw))) {
        if (!isNegated(corpus, 'prp') && !isNegated(corpus, 'platelet'))
          return 'PRP';
      }
    }

    return null;
  },
};

// ═══════════════════════════════════════════════════════════════
//  SHOULDER-SPECIFIC: MRI TEAR SEVERITY
// ═══════════════════════════════════════════════════════════════

const SHOULDER_MRI_SEVERITY: RuleDefinition = {
  id: 'gap-shoulder-mri',
  cptCodes: ['29827', '29826', '29824', '29823', '29825', '23412', '23410'],
  anatomies: ['shoulder'],
  question: () => 'Does the MRI confirm a full-thickness or partial-thickness rotator cuff tear?',
  fieldType: 'select',
  options: [
    'Full-thickness tear',
    'Partial-thickness tear (>50%)',
    'Partial-thickness tear (<50%)',
    'No tear identified',
  ],
  payerRule: 'MRI Confirmation of Rotator Cuff Pathology',
  resolve: (data) => {
    // ── Gate 1: Exemption Override ──
    if (data.payer_risk_flags?.acute_fracture_avulsion) return 'Full-thickness tear';

    const findings = data.radiographic_findings;
    if (!findings) return null;

    const specifics = (findings.specific_findings || []).join(' ').toLowerCase();
    const grade = (findings.severity_grade || '').toLowerCase();
    const allText = `${specifics} ${grade}`;

    // ── Gate 4: Quantitative severity ONLY ──
    const fullThicknessPatterns = [
      /full[- ]thickness/i,
      /complete\s+tear/i,
      /complete\s+rupture/i,
      /retract(?:ed|ion)/i,
      /avulsion/i,
    ];

    const highGradePartialPatterns = [
      /(?:>|greater than)\s*50\s*%/i,
      /high[- ]grade\s+partial/i,
      /(?:5[1-9]|[6-9]\d|100)\s*%/i,  // Any percentage 51-100%
      /near[- ]complete/i,
      /subtotal/i,
    ];

    if (fullThicknessPatterns.some(rx => rx.test(allText))) {
      const corpus = buildCorpus(data);
      if (isNegated(corpus, 'full.thickness') || isNegated(corpus, 'complete tear')) return null;
      return 'Full-thickness tear';
    }

    if (highGradePartialPatterns.some(rx => rx.test(allText))) {
      const corpus = buildCorpus(data);
      if (isNegated(corpus, 'partial') || isNegated(corpus, 'high.grade')) return null;
      return 'Partial-thickness tear (>50%)';
    }

    // AMBIGUITY TRAP: "partial", "incomplete", "fraying", "tendinosis" → NEVER auto-resolve
    const ambiguousPatterns = [
      /partial/i, /incomplete/i, /fraying/i, /tendinosis/i, /tendinopathy/i, /degenerative/i,
    ];
    if (ambiguousPatterns.some(rx => rx.test(allText))) {
      return null; // Force surgeon to specify severity grade
    }

    // MRI exists but no tear language at all
    if (findings.imaging_modality?.toLowerCase().includes('mri')) {
      const corpus = buildCorpus(data);
      if (corpus.includes('no tear') || corpus.includes('intact rotator cuff'))
        return 'No tear identified';
    }

    return null;
  },
};

// ═══════════════════════════════════════════════════════════════
//  SPINE-SPECIFIC: NEUROLOGIC DEFICIT
// ═══════════════════════════════════════════════════════════════

const SPINE_NEURO_DEFICIT: RuleDefinition = {
  id: 'gap-spine-neuro',
  cptCodes: ['63047', '63048', '22630', '22633', '22612', '22614', '63030', '63042'],
  anatomies: ['spine'],
  question: () => 'Is there a documented progressive neurologic deficit (e.g., foot drop, cauda equina, myelopathy)?',
  fieldType: 'toggle',
  payerRule: 'Neurologic Deficit Documentation (Spine)',
  resolve: (data) => {
    // ── Gate 1: Exemption ──
    if (data.payer_risk_flags?.neurologic_deficit) return 'yes';

    const corpus = buildCorpus(data);
    const neuroPatterns = [
      /foot\s+drop/i,
      /cauda\s+equina/i,
      /myelopathy/i,
      /progressive\s+(?:weakness|numbness|deficit)/i,
      /bowel\s+(?:or\s+)?bladder\s+(?:dysfunction|incontinence)/i,
    ];

    const hasNeuro = neuroPatterns.some(rx => rx.test(corpus));
    if (!hasNeuro) return null;

    // ── Gate 4: Negation ──
    if (isNegated(corpus, 'foot drop') || isNegated(corpus, 'myelopathy') ||
        isNegated(corpus, 'cauda equina') || isNegated(corpus, 'weakness')) return null;

    return 'yes';
  },
};

// ═══════════════════════════════════════════════════════════════
//  KNEE-SPECIFIC: WEIGHT-BEARING X-RAY
// ═══════════════════════════════════════════════════════════════

const KNEE_XRAY: RuleDefinition = {
  id: 'gap-xray',
  cptCodes: ['27447', '27446'],
  anatomies: ['knee'],
  question: () => 'Did the patient have a weight-bearing AP X-ray showing bone-on-bone (KL Grade 3+)?',
  fieldType: 'toggle',
  payerRule: 'Radiographic Evidence Requirement (Knee)',
  resolve: (data) => {
    // ── Gate 1 ──
    if (data.payer_risk_flags?.bone_on_bone_exemption) return 'yes';

    const findings = data.radiographic_findings;
    if (!findings) return null;

    const grade = (findings.severity_grade || '').toLowerCase();
    const specifics = (findings.specific_findings || []).join(' ').toLowerCase();
    const allText = `${grade} ${specifics}`;

    const validTriggers = [
      /kl\s*(grade\s*)?[34]/i,
      /grade\s*[34]/i,
      /kellgren[- ]?lawrence\s*[34]/i,
      /bone[- ]on[- ]bone/i,
    ];

    if (!validTriggers.some(rx => rx.test(allText))) return null;

    const corpus = buildCorpus(data);
    if (isNegated(corpus, 'bone.on.bone') || isNegated(corpus, 'grade 3') || isNegated(corpus, 'grade 4'))
      return null;

    return 'yes';
  },
};

// ═══════════════════════════════════════════════════════════════
//  REGISTRY & PUBLIC API
// ═══════════════════════════════════════════════════════════════

const ALL_RULES: RuleDefinition[] = [
  UNIVERSAL_PT_DURATION,
  UNIVERSAL_IMAGING,
  UNIVERSAL_INJECTION,
  SHOULDER_MRI_SEVERITY,
  SPINE_NEURO_DEFICIT,
  KNEE_XRAY,
];

/**
 * Determines the target anatomy for a set of CPT codes.
 * Uses procedure_context if available, otherwise infers from CPT codes.
 */
function resolveAnatomy(activeCptCodes: string[], data: ClinicalSeverityData | null | undefined): TargetAnatomy {
  if (data?.procedure_context?.target_anatomy && data.procedure_context.target_anatomy !== 'unspecified') {
    return data.procedure_context.target_anatomy;
  }
  // Infer from CPT codes — use first match
  for (const cpt of activeCptCodes) {
    const inferred = inferAnatomyFromCPT(cpt);
    if (inferred !== 'unspecified') return inferred;
  }
  return 'unspecified';
}

/**
 * Checks if a rule applies to the given CPT codes and anatomy.
 */
function ruleApplies(rule: RuleDefinition, activeCptCodes: Set<string>, anatomy: TargetAnatomy): boolean {
  // Check CPT match — empty cptCodes = universal
  const cptMatch = rule.cptCodes.length === 0 || rule.cptCodes.some(c => activeCptCodes.has(c));
  if (!cptMatch) return false;

  // Check anatomy match — empty anatomies = universal
  if (rule.anatomies.length === 0) return true;
  return rule.anatomies.includes(anatomy);
}

/**
 * Given active CPT codes and AI clinical data, build the list of
 * VulnerabilityGap objects using the strict 4-Gate Architecture.
 *
 * Returns ONLY gaps relevant to the active CPT codes and anatomy.
 * Gaps fully resolved by AI are marked with aiResolvedValue.
 */
export function buildDynamicGaps(
  activeCptCodes: string[],
  payerName: string,
  clinicalData: ClinicalSeverityData | null | undefined,
): VulnerabilityGap[] {
  const activeSet = new Set(activeCptCodes.map(c => c.trim()));
  const anatomy = resolveAnatomy(activeCptCodes, clinicalData);
  const seenRuleIds = new Set<string>();
  const gaps: VulnerabilityGap[] = [];

  // Run Gate 2 (Coding) once for the whole case — errors surface as additional gaps
  if (clinicalData) {
    const codingErrors = runCodingGate(clinicalData, anatomy);
    for (const err of codingErrors) {
      gaps.push({
        id: `coding-error-${gaps.length}`,
        cptCode: clinicalData.procedure_context?.primary_cpt || activeCptCodes[0] || '',
        question: err.reason,
        fieldType: 'toggle',
        payerRule: 'ICD-10 / Biomarker Compliance',
        aiResolvedValue: null,
      });
    }
  }

  for (const rule of ALL_RULES) {
    if (!ruleApplies(rule, activeSet, anatomy)) continue;
    if (seenRuleIds.has(rule.id)) continue;
    seenRuleIds.add(rule.id);

    // Find a matched CPT for display
    const matchedCpt = rule.cptCodes.length > 0
      ? rule.cptCodes.find(code => activeSet.has(code)) || rule.cptCodes[0]
      : activeCptCodes[0] || '';

    let aiResolvedValue: string | null = null;
    if (clinicalData) {
      aiResolvedValue = rule.resolve(clinicalData, anatomy);
    }

    gaps.push({
      id: rule.id,
      cptCode: matchedCpt,
      question: rule.question(payerName),
      fieldType: rule.fieldType,
      options: rule.options,
      payerRule: rule.payerRule,
      aiResolvedValue,
    });
  }

  return gaps;
}
